import { createParser } from 'eventsource-parser'

import {
  ASK_DISABLED_ERROR,
  ASK_QUOTA_EXCEEDED_ERROR,
  AskDisabledError,
  AskQuotaExceededError,
  AskRateLimitedError,
  CREDITS_EMPTY_ERROR,
  CreditsEmptyError,
  SearchError,
  SUPER_NO_SUBSCRIPTION_ERROR,
  SuperNoSubscriptionError,
} from '../errors'
import { isSearchErrorResponse } from '../search'

/**
 * The Ask quota resets through the monthly rotation of the Redis key built in
 * `getQuotaPeriodKey` (`yyyyMM`), not through the rate limiter window — whose
 * `ratelimit-reset` header advertises `windowMs`, a year out. The boundary
 * depends on nothing but the calendar, so it is derived here instead. Midday
 * rather than midnight keeps the 1st the 1st when the date is formatted back
 * in a timezone the reader picked over their own.
 */
export function getAskQuotaResetDate(now = new Date()) {
  return new Date(now.getFullYear(), now.getMonth() + 1, 1, 12)
}

export async function fetchSSE<T>({
  onData,
  onError = (error) => {
    throw error
  },
  fetchInput,
  fetchInit,
  fetch,
  data,
  sseEventHandler,
  shouldStop = () => false,
}: {
  shouldStop?: () => boolean
  onData: (data: T) => void
  onError?: (error: Error) => void
  fetchInput: Parameters<typeof globalThis.fetch>[0]
  fetchInit: Parameters<typeof globalThis.fetch>[1] & {
    reactNative?: { textStreaming: true }
  }
  fetch?: typeof globalThis.fetch
  data?: T
  sseEventHandler: (
    data: T | undefined,
    onData: (data: T) => void,
    onError: (error: Error) => void
  ) => Parameters<typeof createParser>[0]
}) {
  const fetchFn = fetch ?? globalThis.fetch
  const response = await fetchFn(fetchInput, fetchInit)
  const headers = response.headers

  if (!response.ok) {
    const errorData = await response.json()
    if (!isSearchErrorResponse(errorData)) {
      return onError(
        new SearchError('An unknown error ocurred', 'unknown', response.status)
      )
    }
    if (response.status === 429) {
      if (errorData.id === ASK_QUOTA_EXCEEDED_ERROR) {
        const limit = headers.get('ratelimit-limit')
        return onError(
          new AskQuotaExceededError('Ask quota exceeded', {
            limit: limit ? parseInt(limit, 10) : null,
            resetDate: getAskQuotaResetDate().toISOString(),
          })
        )
      }
      return onError(new AskRateLimitedError('Ask rate limit exceeded'))
    }
    if (response.status === 422) {
      if (errorData.id === ASK_DISABLED_ERROR) {
        return onError(new AskDisabledError('Ask quota exceeded'))
      }
    }
    if (response.status === 403) {
      if (errorData.id.includes(SUPER_NO_SUBSCRIPTION_ERROR)) {
        return onError(new SuperNoSubscriptionError(errorData.message))
      }
    }
    if (response.status === 402) {
      if (errorData.id === CREDITS_EMPTY_ERROR) {
        return onError(
          new CreditsEmptyError(errorData.message, {
            resetDate: headers.get('credits-reset-at'),
          })
        )
      }
    }
    return onError(
      new SearchError(errorData.message, errorData.id, errorData.status)
    )
  }

  const sseStream = response.body

  if (!sseStream) {
    return onError(new Error('A network error occured.'))
  }

  const parser = createParser(sseEventHandler(data, onData, onError))
  const reader = sseStream?.getReader()
  const textDecoder = new TextDecoder('utf-8')

  async function readChunk() {
    if (shouldStop()) {
      reader.releaseLock()
      await sseStream?.cancel()
      return
    }
    const { done, value } = await reader.read()
    parser.feed(textDecoder.decode(value))
    if (!done) {
      await readChunk()
    }
  }

  await readChunk()

  parser.reset()
}
